import React, { useState, useEffect } from "react";
import styled from "styled-components";
import apiService from "../../services/api";

const FormContainer = styled.div`
  max-width: 800px;
  background-color: #FFFFFF;
  border-radius: 11px;
  margin: 20px;
  padding: 0 24px 24px 24px;
  box-sizing: border-box;
  gap: 16px;
  overflow-y: auto;
`;

const FormStyled = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
`;

const Label = styled.label`
  font-size: 14px; 
  margin-bottom: 6px;
  font-weight: ${props => props.bold ? "bold" : "normal"};
  gap: 4px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 9px;
  font-size: 14px;
  box-sizing: border-box;
  margin-bottom: 6px;

  &:focus {
    border-color: #89c2a1;
    outline: none;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 9px;
  font-size: 14px;
  background-color: #fff;
  cursor: pointer;
  margin-bottom: 6px;
`;

const Button = styled.button`
  padding: 10px 24px;
  background-color: #6AB8A8;
  color: white;
  border: none;
  border-radius: 9px;
  cursor: pointer;
  font-size: 16px;
  margin: 0;
  align-self: flex-end;
  min-width: 120px;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.223);

  &:hover {
    background-color: #56a998ff;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.div`
  color: #d00;
  margin-top: 4px;
  font-size: 12px;
`;

const SuccessText = styled.div`
  color: green;
  margin-top: 4px;
  font-size: 13px;
`;

const MessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 10px;
`;

const ActionsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin: 20px 0;
`;

const VoltarButton = styled.button`
  background-color: #999;
  color: #ffffff;
  border: none;
  padding: 8px 24px;
  font-size: 15px;
  font-weight: 600;
  border-radius: 8px;
  box-shadow: 0px 4px 3px rgba(0, 0, 0, 0.2);
  cursor: pointer;

  &:hover {
    background-color: #888;
  }
`;

const isJwt = (t) => /^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+$/.test(t);
const baseUrl = process.env.REACT_APP_API_BASE_URL;

const CampusForm = ({ editData = null, onVoltar }) => {
  const [form, setForm] = useState({
    campus: "",
    diretor: "",
    endereco: "",
    numero: "",
    bairro: "",
    cidadeId: "",
    estado: "",
    cep: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [cidades, setCidades] = useState([]);
  const [loadingCidades, setLoadingCidades] = useState(false);
  const [estados, setEstados] = useState([]);
  const [loadingEstados, setLoadingEstados] = useState(true);

  // Carregar estados na inicialização
  useEffect(() => {
    const loadEstados = async () => {
      try {
        setLoadingEstados(true);
        const estadosData = await apiService.getEstados();
        setEstados(estadosData);
      } catch (error) {
        console.error('Erro ao carregar estados:', error);
        setError('Erro ao carregar lista de estados.');
      } finally {
        setLoadingEstados(false);
      }
    };

    loadEstados();
  }, []);

  useEffect(() => {
    const loadCidades = async () => {
      if (!form.estado) {
        setCidades([]);
        return;
      }

      try {
        setLoadingCidades(true);
        const estadoSelecionado = estados.find(estado => estado.sigla === form.estado);
        
        if (estadoSelecionado) {
          const cidadesData = await apiService.getCidadesByEstado(estadoSelecionado.id);
          setCidades(cidadesData);
        } else {
          setCidades([]);
        }
      } catch (error) {
        console.error('Erro ao carregar cidades:', error);
        setError('Erro ao carregar lista de cidades.');
      } finally {
        setLoadingCidades(false);
      }
    };

    loadCidades();
  }, [form.estado, estados]);

  useEffect(() => {
    if (editData) {
      setForm({
        campus: editData.campus || "",
        diretor: editData.diretor || "",
        endereco: editData.endereco || "",
        numero: editData.numero || "",
        bairro: editData.bairro || "",
        cidadeId: editData.cidadeId?.toString() || "",
        estado: editData.estado || "",
        cep: editData.cep || "",
      });
    }
  }, [editData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'estado') {
      setForm(prev => ({ ...prev, [name]: value, cidadeId: "" }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
  };

  // validação mínima (exemplo)
  const validate = () => {
    if (!form.campus.trim()) return "Campus é obrigatório";
    if (!form.diretor.trim()) return "Diretor é obrigatório";
    if (!form.endereco.trim()) return "Endereço é obrigatório";
    if (!form.numero.trim()) return "Número é obrigatório";
    if (!form.bairro.trim()) return "Bairro é obrigatório";
    if (!form.cidadeId) return "Cidade é obrigatória";
    if (!form.estado.trim()) return "Estado é obrigatório";
    if (!form.cep.trim()) return "CEP é obrigatório";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    const payload = {
      campus: form.campus,
      diretor: form.diretor,
      endereco: form.endereco,
      numero: form.numero,
      bairro: form.bairro,
      cidadeId: Number(form.cidadeId),
      estado: form.estado,
      cep: form.cep,
      status: 'ATIVO',
    };

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Token de autenticação não encontrado. Faça login.");
      }
      if (!isJwt(token)) {
        throw new Error("Token de autenticação malformado.");
      }

      const isEditing = editData && editData.id;
      const url = isEditing 
        ? `${baseUrl}/campis/${editData.id}` 
        : `${baseUrl}/campis`;
      const method = isEditing ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        let errMsg;
        try {
          const errBody = await res.json();
          errMsg = errBody.message || JSON.stringify(errBody);
        } catch {
          errMsg = await res.text();
        }
        throw new Error(`Erro do servidor: ${res.status} — ${errMsg}`);
      }

      const successMessage = isEditing 
        ? "Campus atualizado com sucesso!" 
        : "Campus cadastrado com sucesso!";
      
      setSuccess(successMessage);

      setTimeout(() => {
        window.location.href = "/Campis";
      }, 1500);

      if (!isEditing) {
        setForm({
          campus: "",
          diretor: "",
          endereco: "",
          numero: "",
          bairro: "",
          cidadeId: "",
          estado: "",
          cep: "",
        });
      }

    } catch (err) {
      setError(err.message || "Falha ao salvar");
    } finally {
      setLoading(false);
    }
  };

  const isEditing = editData && editData.id;

  return (
    <FormContainer>
      <FormStyled onSubmit={handleSubmit}>
        <div>
          <Label bold>Campus*</Label>
          <Input
            type="text"
            name="campus"
            value={form.campus}
            onChange={handleChange}
          />
        </div>
        <div>
          <Label>Diretor do campus*</Label>
          <Input
            type="text"
            name="diretor"
            value={form.diretor}
            onChange={handleChange}
          />
        </div>
        <Row>
          <div style={{ flex: 2 }}>
            <Label>Endereço*</Label>
            <Input
              type="text"
              name="endereco"
              value={form.endereco}
              onChange={handleChange}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Label>Número*</Label>
            <Input
              type="text"
              name="numero"
              value={form.numero}
              onChange={handleChange}
            />
          </div>
        </Row>
        <Row>
          <div style={{ flex: 1 }}>
            <Label>Bairro*</Label>
            <Input
              type="text"
              name="bairro"
              value={form.bairro}
              onChange={handleChange}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Label>Estado*</Label>
            <Select
              name="estado"
              value={form.estado}
              onChange={handleChange}
              disabled={loadingEstados}
            >
              <option value="">
                {loadingEstados ? "Carregando estados..." : "Selecione um estado"}
              </option>
              {estados.map((estado) => (
                <option key={estado.id} value={estado.sigla}>
                  {estado.sigla} - {estado.nome}
                </option>
              ))}
            </Select>
            {estados.length === 0 && !loadingEstados && (
              <div style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px' }}>
                Nenhum estado encontrado.
              </div>
            )}
          </div>
        </Row>
        <div>
          <Label>Cidade*</Label>
          <Select
            name="cidadeId"
            value={form.cidadeId}
            onChange={handleChange}
            disabled={loadingCidades || !form.estado}
          >
            <option value="">
              {!form.estado ? "Selecione um estado primeiro" : 
               loadingCidades ? "Carregando cidades..." : "Selecione uma cidade"}
            </option>
            {cidades.map((cidade) => (
              <option key={cidade.id} value={cidade.id}>
                {cidade.nome}
              </option>
            ))}
          </Select>
          {cidades.length === 0 && form.estado && !loadingCidades && (
            <div style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px' }}>
              Nenhuma cidade encontrada para este estado.
            </div>
          )}
        </div>
        <div>
          <Label>CEP*</Label>
          <Input
            type="text"
            name="cep"
            value={form.cep}
            onChange={handleChange}
          />
        </div>
        
        <MessageContainer>
          {error && <ErrorText>{error}</ErrorText>}
          {success && <SuccessText>{success}</SuccessText>}
        </MessageContainer>

        <ActionsRow>
          {onVoltar ? (
            <VoltarButton type="button" onClick={onVoltar} disabled={loading}>
              Voltar
            </VoltarButton>
          ) : (
            <span />
          )}
          <Button type="submit" disabled={loading}>
            {loading ? "Salvando..." : isEditing ? "Atualizar" : "Salvar"}
          </Button>
        </ActionsRow>
      </FormStyled>
    </FormContainer>
  );
};

export default CampusForm;
